import { ButtonHTMLAttributes, ReactNode } from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import clsx from "clsx";

import { sizeOptions } from "./grid/page-size";

const viewablePages = 5;

const getPageRange = (currentPage: number = 1, totalPages: number = 1) => {
  const hasMorePages = totalPages > viewablePages;
  const pageLimit = !hasMorePages ? totalPages - 1 : viewablePages - 1;
  let pageStart = 1;

  if (hasMorePages) {
    const pageOffset = Math.floor(viewablePages / 2);
    const nearEnd = currentPage > totalPages - pageOffset;
    const nearStart = currentPage <= pageOffset;
    if (nearEnd) {
      pageStart = totalPages - viewablePages + 1;
    } else if (!nearStart) {
      pageStart = currentPage - pageOffset;
    }
  }
  let pages: number[] = [];

  while (pages.length <= pageLimit) {
    pages.push(pages.length ? pages[pages.length - 1] + 1 : pageStart);
  }

  return pages;
};

type PageButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  onClick: () => void;
  isActive?: boolean;
};

const PageButton = ({
  children,
  className = "px-2 py-1 md:px-2.5",
  onClick,
  isActive,
  disabled,
  ...rest
}: PageButtonProps) => (
  <button
    type="button"
    className={clsx(
      className,
      "inline-flex items-center font-medium rounded-md text-xs md:text-sm",
      isActive ? "bg-primary text-white" : "text-gray-400",
      disabled && "opacity-50 cursor-not-allowed",
      !isActive && !disabled && "hover:bg-gray-100 hover:text-gray-600"
    )}
    onClick={onClick}
    disabled={disabled}
    {...rest}
  >
    {children}
  </button>
);

type PaginationProps = {
  currentPage?: number;
  totalCount?: number;
  pageSize?: number;
  onChange: (page: number) => void;
};

const Pagination = ({
  currentPage = 1,
  totalCount = 0,
  pageSize = Number(sizeOptions[0]),
  onChange,
}: PaginationProps) => {
  if (!totalCount) {
    return null;
  }

  const totalPages = Math.ceil(totalCount / pageSize);
  const activePage = Math.max(1, currentPage || 1);
  const pageEnd = Math.min(activePage, totalPages);
  const pages = getPageRange(activePage, totalPages);

  return (
    <nav className="inline-flex items-center px-2 py-1 bg-white rounded-lg gap-1">
      <PageButton
        className="p-1"
        onClick={() => onChange(activePage - 1)}
        disabled={activePage === 1}
        aria-label="Previous page"
      >
        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
      </PageButton>
      <div className="flex gap-1">
        {pages.map(page => (
          <PageButton
            key={page}
            onClick={() => onChange(page)}
            isActive={activePage === page}
            aria-label={`Page ${page}`}
          >
            {page}
          </PageButton>
        ))}
      </div>
      <PageButton
        className="p-1"
        onClick={() => onChange(activePage + 1)}
        disabled={pageEnd === totalPages}
        aria-label="Next page"
      >
        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
      </PageButton>
    </nav>
  );
};

export default Pagination;
